import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { apiFetch } from '@/stores/authStore';

// Per-ticker view of the logged-in user's open Kalshi exposure.
// Positions are summed per side so the POS chip can render a proportional
// YES/NO arc when a user holds both. Orders carry their Kalshi-native
// queue_position; cumulative "level + better" depth is computed at render
// time against the live orderbook (so it ticks with hydra without a refetch).
//
// Wire: a single relay round-trip per poll. /api/kalshi/me/snapshot returns
// {positions, orders} pre-filtered to the calling user and (when supplied)
// to the visible ticker set. queue_position_fp is merged into each order
// server-side so the hook just normalizes one payload.

export interface UserPositionSlice {
  yesCount: number;
  noCount: number;
}

export interface UserOrderSlice {
  orderId: string;
  side: 'yes' | 'no';
  action: 'buy' | 'sell';
  priceCents: number;
  remainingCount: number;
  queuePosition: number | null;
}

export interface UserKalshiPOS {
  positionByTicker: Map<string, UserPositionSlice>;
  ordersByTicker: Map<string, UserOrderSlice[]>;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

interface RawPosition {
  ticker?: unknown;
  side?: unknown;
  netCount?: unknown;
}

interface RawOrder {
  order_id?: unknown;
  ticker?: unknown;
  side?: unknown;
  action?: unknown;
  yes_price_dollars?: unknown;
  no_price_dollars?: unknown;
  remaining_count_fp?: unknown;
  yes_price?: unknown;
  no_price?: unknown;
  remaining_count?: unknown;
  queue_position_fp?: unknown;
  queue_position?: unknown;
}

function asString(v: unknown): string | null {
  return typeof v === 'string' ? v : null;
}
function asNumber(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}
function asFloat(v: unknown): number | null {
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  if (typeof v === 'string') {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function extractList<T>(payload: unknown, key: string): T[] {
  if (!payload || typeof payload !== 'object') return [];
  const list = (payload as Record<string, unknown>)[key];
  return Array.isArray(list) ? (list as T[]) : [];
}

const POLL_MS = 5000;

export function useUserKalshiPOS(tickers: string[]): UserKalshiPOS {
  const [positionByTicker, setPositionByTicker] = useState<Map<string, UserPositionSlice>>(
    () => new Map(),
  );
  const [ordersByTicker, setOrdersByTicker] = useState<Map<string, UserOrderSlice[]>>(
    () => new Map(),
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const tickerKey = useMemo(() => tickers.slice().sort().join('|'), [tickers]);
  const tickerKeyRef = useRef(tickerKey);
  tickerKeyRef.current = tickerKey;

  const fetchAll = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      const url = tickerKeyRef.current
        ? `/api/kalshi/me/snapshot?market_tickers=${encodeURIComponent(tickerKeyRef.current.replace(/\|/g, ','))}`
        : '/api/kalshi/me/snapshot';
      const res = await apiFetch(url);
      const json: unknown = res.ok ? await res.json() : null;

      const rawPositions = extractList<RawPosition>(json, 'positions');
      const rawOrders = extractList<RawOrder>(json, 'orders');

      const posMap = new Map<string, UserPositionSlice>();
      for (const p of rawPositions) {
        const ticker = asString(p.ticker);
        const side = asString(p.side);
        const net = asNumber(p.netCount);
        if (!ticker || !net || net <= 0) continue;
        if (side !== 'yes' && side !== 'no') continue;
        const slice = posMap.get(ticker) ?? { yesCount: 0, noCount: 0 };
        if (side === 'yes') slice.yesCount += net;
        else slice.noCount += net;
        posMap.set(ticker, slice);
      }

      const ordMap = new Map<string, UserOrderSlice[]>();
      for (const o of rawOrders) {
        const orderId = asString(o.order_id);
        const ticker = asString(o.ticker);
        const sideRaw = asString(o.side);
        const actionRaw = asString(o.action);
        const remaining =
          asFloat(o.remaining_count_fp) ?? asNumber(o.remaining_count) ?? 0;
        if (!orderId || !ticker || remaining <= 0) continue;
        if (sideRaw !== 'yes' && sideRaw !== 'no') continue;
        if (actionRaw !== 'buy' && actionRaw !== 'sell') continue;
        const yesDollars = asFloat(o.yes_price_dollars);
        const noDollars = asFloat(o.no_price_dollars);
        const yesPx = yesDollars != null ? Math.round(yesDollars * 100) : asNumber(o.yes_price);
        const noPx = noDollars != null ? Math.round(noDollars * 100) : asNumber(o.no_price);
        const priceCents = sideRaw === 'yes' ? yesPx : noPx;
        if (priceCents == null) continue;
        const queuePosition = asFloat(o.queue_position_fp) ?? asNumber(o.queue_position);
        const slice: UserOrderSlice = {
          orderId,
          side: sideRaw,
          action: actionRaw,
          priceCents,
          remainingCount: remaining,
          queuePosition,
        };
        const list = ordMap.get(ticker) ?? [];
        list.push(slice);
        ordMap.set(ticker, list);
      }

      setPositionByTicker(posMap);
      setOrdersByTicker(ordMap);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load activity');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchAll();
    const id = setInterval(() => {
      void fetchAll();
    }, POLL_MS);
    return () => clearInterval(id);
  }, [fetchAll, tickerKey]);

  return { positionByTicker, ordersByTicker, loading, error, refresh: fetchAll };
}
